import { supabase } from '@/integrations/supabase/client';
import { DatabaseCourseService } from './DatabaseCourseService';
import { Course } from '@/hooks/useCourses';
import { Module } from '@/types/courseTypes';
import { logger } from '@/utils/logger';

/**
 * Enhanced Database Service with caching, validation, and performance optimization
 */
export class EnhancedDatabaseService extends DatabaseCourseService {
  private static cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private static readonly DEFAULT_TTL = 30 * 60 * 1000; // 30 minutes (increased for performance)
  private static readonly MODULE_CACHE_TTL = 60 * 60 * 1000; // 60 minutes for modules
  private static readonly COURSE_CACHE_TTL = 30 * 60 * 1000; // 30 minutes for courses

  /**
   * Wrap a promise with a timeout - returns fallback on timeout
   */
  private static async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    fallback: T,
    operation: string
  ): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => 
        setTimeout(() => reject(new Error(`${operation} timeout after ${timeoutMs}ms`)), timeoutMs)
      )
    ]).catch(error => {
      logger.warn(`⏱️ ${operation} timed out, using fallback`, { error: error.message });
      return fallback;
    });
  }

  /**
   * Get data with caching support
   */
  private static async getCached<T>(
    key: string, 
    fetcher: () => Promise<T>, 
    ttl: number = this.DEFAULT_TTL
  ): Promise<T> {
    const cached = this.cache.get(key);
    
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }

    try {
      const data = await fetcher();
      this.cache.set(key, { data, timestamp: Date.now(), ttl });
      return data;
    } catch (error) {
      logger.error(`Error fetching ${key}:`, error);
      // Return cached data if available, even if expired
      if (cached) return cached.data;
      throw error;
    }
  }

  /**
   * Clear cache for specific key or all cache
   */
  static clearCache(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Clear module cache after content enhancement
   */
  static clearModuleCache(moduleId: string, courseId: string): void {
    this.clearCache(`complete-module-${courseId}-${moduleId}`);
    this.clearCache(`course-with-content-${courseId}`);
    logger.info(`🗑️ Cleared cache for module ${moduleId} in course ${courseId}`);
  }

  /**
   * Get all courses with caching
   */
  static async getAllCoursesCached(): Promise<Course[]> {
    return this.getCached(
      'all-courses',
      () => super.getAllCourses(),
      this.COURSE_CACHE_TTL // 30 minutes for courses
    );
  }

  /**
   * Get courses by category with caching
   */
  static async getCoursesByCategoryCached(category: string): Promise<Course[]> {
    return this.getCached(
      `courses-category-${category}`,
      () => super.getCoursesByCategory(category)
    );
  }

  /**
   * Get course with modules and content (optimized single query)
   */
  static async getCourseWithContent(courseId: string): Promise<Course & { modules: Module[] } | null> {
    return this.getCached(
      `course-with-content-${courseId}`,
      async () => {
        const [course, modules] = await Promise.all([
          super.getCourseById(courseId),
          super.getCourseModules(courseId)
        ]);

        if (!course) return null;

        return {
          ...course,
          modules
        };
      }
    );
  }

  /**
   * Get complete module with all related data in one optimized call
   * Using longer TTL for better performance
   * CACHE INVALIDATION: Returns null for disabled modules and clears cache
   */
  static async getCompleteModuleCached(moduleId: string, courseId: string): Promise<Module | null> {
    const cacheKey = `complete-module-${courseId}-${moduleId}`;
    
    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      // Verify cached module is enabled
      if (cached.data && 'is_enabled' in cached.data && cached.data.is_enabled === false) {
        this.clearCache(cacheKey);
        logger.info(`🗑️ Cleared cache for disabled module: ${moduleId}`);
        return null;
      }
      return cached.data;
    }
    
    // Fetch from database
    const module = await super.getCompleteModule(moduleId, courseId);
    
    // Don't cache disabled modules
    if (module && 'is_enabled' in module && module.is_enabled === false) {
      logger.info(`⚠️ Module ${moduleId} is disabled, not caching`);
      return null;
    }
    
    // Cache enabled modules
    if (module) {
      this.cache.set(cacheKey, { 
        data: module, 
        timestamp: Date.now(), 
        ttl: this.MODULE_CACHE_TTL 
      });
    }
    
    return module;
  }

  /**
   * Prefetch adjacent modules for instant navigation
   */
  static async prefetchAdjacentModules(courseId: string, currentModuleId: string): Promise<void> {
    try {
      const modules = await this.getCached(
        `course-modules-${courseId}`,
        () => super.getCourseModules(courseId),
        this.COURSE_CACHE_TTL
      );
      
      const currentIndex = modules.findIndex(m => m.id === currentModuleId);
      
      // Prefetch next 2 modules in background (non-blocking)
      if (currentIndex >= 0 && currentIndex < modules.length - 1) {
        this.getCompleteModuleCached(modules[currentIndex + 1].id, courseId).catch(() => {});
      }
      if (currentIndex >= 0 && currentIndex < modules.length - 2) {
        this.getCompleteModuleCached(modules[currentIndex + 2].id, courseId).catch(() => {});
      }
    } catch (error) {
      // Silently fail prefetch - not critical
      logger.error('Error prefetching modules:', error);
    }
  }

  /**
   * Preload all modules for a course (called when entering course page)
   */
  static async preloadCourseModules(courseId: string, limit: number = 3): Promise<void> {
    try {
      const modules = await this.getCached(
        `course-modules-${courseId}`,
        () => super.getCourseModules(courseId),
        this.COURSE_CACHE_TTL
      );
      
      // Preload first N modules in parallel
      await Promise.all(
        modules.slice(0, limit).map(m => 
          this.getCompleteModuleCached(m.id, courseId).catch(() => {})
        )
      );
      
      logger.info(`✅ Preloaded ${Math.min(limit, modules.length)} modules for course ${courseId}`);
    } catch (error) {
      logger.error('Error preloading course modules:', error);
    }
  }

  /**
   * PHASE 1 OPTIMIZATION: Get minimal module shell (content only) - FAST
   * Target: 100-200ms for text content
   * With 3-second timeout protection
   */
  static async getModuleShell(moduleId: string, courseId: string): Promise<Module | null> {
    const cacheKey = `module-shell-${courseId}-${moduleId}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      logger.info(`⚡ Cache hit for module shell: ${moduleId}`);
      return cached.data;
    }

    try {
      performance.mark('module-shell-start');
      
      // TIER 1: Only critical data - minimal queries WITH TIMEOUT
      const queries = Promise.all([
        supabase
          .from('course_modules')
          .select('module_id, module_title, module_order, estimated_duration, difficulty_level, prerequisites')
          .eq('module_id', moduleId)
          .eq('course_id', courseId)
          .eq('is_enabled', true)
          .single(),
        supabase
          .from('module_content')
          .select('content, objectives, key_concepts')
          .eq('module_id', moduleId)
          .eq('course_id', courseId)
          .maybeSingle()
      ]);

      const [moduleData, content] = await this.withTimeout(
        queries,
        3000, // 3s timeout for shell queries
        [{ data: null, error: null, count: null, status: 0, statusText: '' }, { data: null, error: null, count: null, status: 0, statusText: '' }] as any,
        'Module shell load'
      );

      performance.mark('module-shell-end');
      performance.measure('module-shell-time', 'module-shell-start', 'module-shell-end');
      const measure = performance.getEntriesByName('module-shell-time')[0];
      logger.info(`⚡ Module shell loaded in ${Math.round(measure.duration)}ms`);

      if (!moduleData.data) {
        return null;
      }

      const shell: Module = {
        id: moduleData.data.module_id,
        title: moduleData.data.module_title,
        description: '',
        content: content.data?.content || '',
        duration: moduleData.data.estimated_duration ? `${moduleData.data.estimated_duration} min` : '30 min',
        order: moduleData.data.module_order,
        estimatedTime: moduleData.data.estimated_duration,
        difficulty: moduleData.data.difficulty_level || undefined,
        prerequisites: moduleData.data.prerequisites || [],
        objectives: content.data?.objectives || [],
        keyConcepts: content.data?.key_concepts || [],
        // Empty arrays for progressive loading
        codeExamples: [],
        exercises: [],
        quickCheck: { questions: [] },
        _isShell: true // Flag to indicate this is minimal data
      } as Module & { _isShell: boolean };

      this.cache.set(cacheKey, { 
        data: shell, 
        timestamp: Date.now(), 
        ttl: 10 * 60 * 1000 // 10min cache for shells
      });

      return shell;
    } catch (error) {
      logger.error('Error loading module shell:', error);
      throw error;
    }
  }

  /**
   * PHASE 1 OPTIMIZATION: Enrich module with examples, exercises, etc. - BACKGROUND
   * Non-blocking progressive enhancement
   * With 5-second timeout protection
   */
  static async enrichModule(shell: Module, moduleId: string, courseId: string): Promise<Module> {
    try {
      performance.mark('module-enrich-start');

      // TIER 2: Important but not critical - load in parallel WITH TIMEOUT
      const [examples, exercises, quiz] = await this.withTimeout(
        Promise.all([
          super.getModuleCodeExamples(moduleId, courseId).catch(() => []),
          super.getModuleExercises(moduleId, courseId).catch(() => []),
          super.getModuleQuiz(moduleId, courseId).catch(() => null)
        ]),
        5000, // 5s timeout for enrichment
        [[], [], null],
        'Module enrichment'
      );

      performance.mark('module-enrich-end');
      performance.measure('module-enrich-time', 'module-enrich-start', 'module-enrich-end');
      const measure = performance.getEntriesByName('module-enrich-time')[0];
      logger.info(`🎨 Module enriched in ${Math.round(measure.duration)}ms`);

      const enriched: Module = {
        ...shell,
        codeExamples: examples.map(ex => ({
          title: ex.title,
          code: ex.code,
          language: ex.language,
          explanation: ex.explanation || ''
        })),
        exercises: exercises.map(ex => ({
          id: ex.id,
          title: ex.title,
          description: ex.description,
          starterCode: ex.starter_code || '',
          solution: ex.solution || '',
          hints: ex.hints || []
        })),
        quickCheck: quiz ? {
          questions: quiz.map((q: any) => ({
            id: q.id,
            question: q.question,
            options: q.options || [],
            correctAnswer: q.correct_answer || q.correctAnswer,
            explanation: q.explanation
          }))
        } : { questions: [] },
        _isShell: false // Now fully loaded
      } as Module & { _isShell: boolean };

      // Cache the enriched version
      this.cache.set(`complete-module-${courseId}-${moduleId}`, {
        data: enriched,
        timestamp: Date.now(),
        ttl: this.MODULE_CACHE_TTL
      });

      return enriched;
    } catch (error) {
      logger.error('Error enriching module:', error);
      return shell; // Return shell if enrichment fails
    }
  }

  /**
   * Search courses with caching and enhanced filtering
   */
  static async searchCoursesEnhanced(query: string, filters?: {
    category?: string;
    level?: string;
    duration?: 'short' | 'medium' | 'long';
  }): Promise<Course[]> {
    const cacheKey = `search-${query}-${JSON.stringify(filters)}`;
    
    return this.getCached(cacheKey, async () => {
      let queryBuilder = supabase
        .from('courses')
        .select('*')
        .eq('is_active', true)
        .or(`title.ilike.%${query}%,description.ilike.%${query}%`);

      if (filters?.category) {
        queryBuilder = queryBuilder.eq('category', filters.category);
      }

      if (filters?.level) {
        queryBuilder = queryBuilder.eq('level', filters.level);
      }

      if (filters?.duration) {
        switch (filters.duration) {
          case 'short':
            queryBuilder = queryBuilder.lte('duration_hours', 2);
            break;
          case 'medium':
            queryBuilder = queryBuilder.gte('duration_hours', 2).lte('duration_hours', 8);
            break;
          case 'long':
            queryBuilder = queryBuilder.gte('duration_hours', 8);
            break;
        }
      }

      const { data, error } = await queryBuilder.order('title');

      if (error) {
        logger.error('Error searching courses:', error);
        throw error;
      }

      return (data || []).map(course => ({
        ...course,
        course_status: (course.course_status || 'active') as 'active' | 'coming_soon' | 'archived' | 'draft'
      }));
    });
  }

  /**
   * Get course statistics and analytics
   */
  static async getCourseAnalytics(courseId?: string): Promise<{
    totalCourses: number;
    totalModules: number;
    avgDuration: number;
    popularCategories: Array<{ category: string; count: number }>;
    recentCourses: Course[];
  }> {
    const cacheKey = courseId ? `analytics-${courseId}` : 'analytics-global';
    
    return this.getCached(cacheKey, async () => {
      const [coursesResult, modulesResult, categoriesResult, recentCoursesResult] = await Promise.all([
        supabase
          .from('courses')
          .select('id, duration_hours, category, created_at')
          .eq('is_active', true),
        supabase
          .from('course_modules')
          .select('course_id')
          .eq('is_enabled', true),
        supabase
          .rpc('get_categories_with_course_counts'),
        supabase
          .from('courses')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(5)
      ]);

      const courses = coursesResult.data || [];
      const modules = modulesResult.data || [];
      const recentCourses = recentCoursesResult.data || [];

      return {
        totalCourses: courses.length,
        totalModules: modules.length,
        avgDuration: courses.reduce((sum, c) => sum + (c.duration_hours || 0), 0) / courses.length,
        popularCategories: (categoriesResult.data || [])
          .map((cat: any) => ({ category: cat.name, count: cat.course_count }))
          .sort((a: any, b: any) => b.count - a.count)
          .slice(0, 5),
        recentCourses: recentCourses.map(course => ({
          ...course,
          course_status: (course.course_status || 'active') as 'active' | 'coming_soon' | 'archived' | 'draft'
        }))
      };
    }, 15 * 60 * 1000); // 15 minutes for analytics
  }

  /**
   * Validate data integrity
   */
  static async validateDataIntegrity(): Promise<{
    issues: string[];
    stats: {
      coursesWithoutModules: number;
      modulesWithoutContent: number;
      orphanedContent: number;
    };
  }> {
    try {
      const [courses, modules, content] = await Promise.all([
        supabase.from('courses').select('id').eq('is_active', true),
        supabase.from('course_modules').select('course_id, module_id').eq('is_enabled', true),
        supabase.from('module_content').select('course_id, module_id')
      ]);

      const issues: string[] = [];
      const courseIds = new Set((courses.data || []).map(c => c.id));
      const modulesByAourse = new Map<string, string[]>();
      const contentModules = new Set((content.data || []).map(c => `${c.course_id}-${c.module_id}`));

      // Group modules by course
      (modules.data || []).forEach(m => {
        if (!modulesByAourse.has(m.course_id)) {
          modulesByAourse.set(m.course_id, []);
        }
        modulesByAourse.get(m.course_id)!.push(m.module_id);
      });

      // Find courses without modules
      const coursesWithoutModules = Array.from(courseIds).filter(courseId => 
        !modulesByAourse.has(courseId) || modulesByAourse.get(courseId)!.length === 0
      );

      // Find modules without content
      const modulesWithoutContent: string[] = [];
      modulesByAourse.forEach((moduleIds, courseId) => {
        moduleIds.forEach(moduleId => {
          if (!contentModules.has(`${courseId}-${moduleId}`)) {
            modulesWithoutContent.push(`${courseId}/${moduleId}`);
          }
        });
      });

      // Find orphaned content
      const orphanedContent = (content.data || []).filter(c => 
        !courseIds.has(c.course_id)
      );

      if (coursesWithoutModules.length > 0) {
        issues.push(`${coursesWithoutModules.length} courses have no modules`);
      }

      if (modulesWithoutContent.length > 0) {
        issues.push(`${modulesWithoutContent.length} modules have no content`);
      }

      if (orphanedContent.length > 0) {
        issues.push(`${orphanedContent.length} content records are orphaned`);
      }

      return {
        issues,
        stats: {
          coursesWithoutModules: coursesWithoutModules.length,
          modulesWithoutContent: modulesWithoutContent.length,
          orphanedContent: orphanedContent.length
        }
      };
    } catch (error) {
      logger.error('Error validating data integrity:', error);
      return {
        issues: ['Error performing validation'],
        stats: { coursesWithoutModules: 0, modulesWithoutContent: 0, orphanedContent: 0 }
      };
    }
  }

  /**
   * Bulk operations for admin interface
   */
  static async bulkUpdateCourses(updates: Array<{ id: string; updates: Partial<Course> }>): Promise<void> {
    const updateRecords = updates.map(u => ({
      id: u.id,
      category: u.updates.category || 'react', // Required field with default
      title: u.updates.title || 'Untitled Course', // Required field with default
      ...u.updates
    }));

    const { error } = await supabase
      .from('courses')
      .upsert(updateRecords);

    if (error) {
      logger.error('Error bulk updating courses:', error);
      throw error;
    }

    // Clear relevant cache entries
    updates.forEach(u => {
      this.clearCache(`course-${u.id}`);
      this.clearCache(`course-with-content-${u.id}`);
    });
    this.clearCache('all-courses');
  }

  /**
   * Performance monitoring
   */
  static getPerformanceStats(): {
    cacheSize: number;
    cacheHitRate: number;
    averageResponseTime: number;
  } {
    return {
      cacheSize: this.cache.size,
      cacheHitRate: 0, // Would need to implement hit tracking
      averageResponseTime: 0 // Would need to implement timing
    };
  }
}